import { Button, Grid, ScrollView, SearchField } from "@aws-amplify/ui-react";
import { useMemo, useState } from "react";
import LocalizedStrings from "react-localization";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import ConfirmModal from "components/utils/ConfirmModal";
import ActionIcon from "components/actions/ActionIcon";
import demoPestMonitoring from "config/demo/pestAndDiseaseMonitoring.json";
import PestMonitoringForm from "components/agro/pestMonitoring/PestMonitoringForm";

const PestAndDiseaseMonitoring = () => {
  const [records] = useState(
    demoPestMonitoring && demoPestMonitoring.slice
      ? demoPestMonitoring.slice(0, 10)
      : []
  );
  const [recordDetails, setRecordDetails] = useState();
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    itemId: null,
    itemName: null,
  });
  const [modalForm, setModalForm] = useState({
    visible: false,
    itemId: null,
    action: null,
  });

  const columnsMapped = useMemo(
    () =>
      records.map((item) => ({
        ...item,
        actions: (
          <>
            <ActionIcon
              name={
                recordDetails?.id === item.id
                  ? strings.monitoring.hide
                  : strings.monitoring.show
              }
              icon={
                recordDetails?.id === item.id ? "VisibilityOff" : "Visibility"
              }
              color="primary"
              onClick={() => {
                recordDetails?.id === item.id
                  ? setRecordDetails(null)
                  : setRecordDetails(item);
              }}
            />
            <ActionIcon
              name={strings.monitoring.edit}
              icon="Edit"
              color="primary"
              onClick={() => openModal("update", item.id)}
            />
            <ActionIcon
              name={strings.monitoring.delete}
              icon="Delete"
              color="red.60"
              onClick={() =>
                setModalDetails({
                  visible: true,
                  itemId: item.id,
                  itemName: item.id,
                })
              }
            />
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [records, recordDetails?.id]
  );

  const openModal = (action, id = null) =>
    setModalForm({ visible: true, action, itemId: id });

  const tableColumns = [
    {
      key: "date",
      label: strings.table.headers.date,
      _style: { width: "18%" },
    },
    {
      key: "farmName",
      label: strings.table.headers.farm,
      _style: { width: "22%" },
    },
    {
      key: "lotName",
      label: strings.table.headers.lot,
      _style: { width: "22%" },
    },
    {
      key: "performedByName",
      label: strings.table.headers.performedBy,
      _style: { width: "20%" },
    },
    { key: "actions", label: "", _style: { width: "18%" } },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      <Button
        style={styles.buttonAdd}
        children={strings.monitoring.addRecord}
        variation="primary"
        onClick={() => openModal("create")}
      />
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
          />
        </Grid>

        <CTable
          className="table-centered"
          items={columnsMapped}
          columns={tableColumns}
          tableHeadProps={{ color: "light" }}
          responsive
          hover
        />

        <CCollapse visible={!!recordDetails?.id} style={styles.collapseCard}>
          <CCard key={recordDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{recordDetails?.id}"
              </h4>

              <p className="text-muted">
                <b>{strings.details.date}:</b>{" "}
                {recordDetails?.date
                  ? moment(recordDetails.date).format("YYYY-MM-DD")
                  : "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.farm}:</b>{" "}
                {recordDetails?.farmName ?? recordDetails?.farmId ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.lot}:</b>{" "}
                {recordDetails?.lotName ?? recordDetails?.lotId ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.activeCropUnits}:</b>{" "}
                {Array.isArray(recordDetails?.activeCropUnits)
                  ? recordDetails.activeCropUnits.join(", ")
                  : recordDetails?.activeCropUnits ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.minUnitsToInspect}:</b>{" "}
                {recordDetails?.minUnitsToInspect ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.performedBy}:</b>{" "}
                {recordDetails?.performedByName ??
                  recordDetails?.performedById ??
                  "-"}
              </p>

              <hr />

              <p className="text-muted">
                <b>{strings.details.inspectionRecords}:</b>{" "}
                {recordDetails?.inspectionRecords
                  ? JSON.stringify(recordDetails.inspectionRecords)
                  : "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.symptomsByGrowthStage}:</b>{" "}
                {recordDetails?.symptomsByGrowthStage
                  ? JSON.stringify(recordDetails.symptomsByGrowthStage)
                  : "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.characterizationChecks}:</b>{" "}
                {recordDetails?.characterizationChecks
                  ? JSON.stringify(recordDetails.characterizationChecks)
                  : "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.importantNotes}:</b>{" "}
                {recordDetails?.importantNotes ?? "-"}
              </p>
              <p className="text-muted">
                <b>{strings.details.photoEvidence}:</b>{" "}
                {(Array.isArray(recordDetails?.photoEvidence)
                  ? recordDetails.photoEvidence.join(", ")
                  : recordDetails?.photoEvidence) ?? "-"}
              </p>

              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {recordDetails?.createdAt
                  ? moment(recordDetails.createdAt).format(
                      "YYYY-MM-DD HH:mm:ss"
                    )
                  : "-"}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>

      <ConfirmModal
        visible={modalDetails.visible}
        body={strings.formatString(strings.modal.confirmDeleteQuestion, {
          name: modalDetails.itemName,
        })}
        onCancel={() => setModalDetails({ visible: false, itemId: null })}
      />

      {modalForm.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalForm.visible}
          onClose={() => setModalForm({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalForm.action === "create"
                ? strings.modalForm.addTitle
                : strings.modalForm.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              <PestMonitoringForm
                type={modalForm.action}
                initialValues={
                  modalForm.action === "update"
                    ? records.find((r) => r.id === modalForm.itemId) || {}
                    : {}
                }
                onSubmit={() => {}}
                onCancel={() => setModalForm({ visible: false })}
              />
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Monitoreo de Plagas y Enfermedades",
    monitoring: {
      addRecord: "Registrar Monitoreo",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    table: {
      headers: {
        date: "Fecha",
        farm: "Finca",
        lot: "Lote",
        performedBy: "Inspector",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles Monitoreo",
      date: "Fecha",
      farm: "Finca",
      lot: "Lote",
      activeCropUnits: "Unidades de cultivo activas",
      minUnitsToInspect: "Unidades mínimas a inspeccionar",
      performedBy: "Persona que realiza",
      inspectionRecords: "Registros de inspección",
      symptomsByGrowthStage: "Síntomas por etapa",
      characterizationChecks: "Caracterización (checks)",
      importantNotes: "Anotaciones importantes",
      photoEvidence: "Evidencia fotográfica",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar el monitoreo {name}?",
    },
    modalForm: {
      addTitle: "Registrar Monitoreo",
      updateTitle: "Actualizar Monitoreo",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  collapseCard: { marginTop: "15px" },
};

export default PestAndDiseaseMonitoring;
